import { AlertTriangle, ChevronDown, ChevronRight } from "lucide-react";
import React, { useState } from "react";

import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { formatRub } from "@/lib/format";
import { attentionKindInfo, type AttentionKindInfo } from "@/pages/cashflow/attentionKinds";
import { orgLabel, sumDecimalStrings } from "@/pages/cashflow/utils";
import type { CashflowAttention, CashflowOrg, CashflowSourceStatus } from "@/types/api";

interface Props {
  attention: CashflowAttention[];
  sources: CashflowSourceStatus[];
  orgs: CashflowOrg[];
}

interface AttentionGroup {
  kind: string;
  info: AttentionKindInfo;
  items: CashflowAttention[];
  /** Σ сумм замечаний группы; `undefined` — сумм нет ни у одного. */
  total: string | undefined;
}

/** Замечания → группы по виду, в порядке первого появления (порядок бэка). */
function groupAttention(attention: CashflowAttention[]): AttentionGroup[] {
  const byKind = new Map<string, CashflowAttention[]>();
  for (const a of attention) {
    const bucket = byKind.get(a.kind);
    if (bucket === undefined) byKind.set(a.kind, [a]);
    else bucket.push(a);
  }
  return [...byKind.entries()].map(([kind, items]) => {
    const amounts = items.map((a) => a.amount).filter((v): v is string => v !== null);
    return {
      kind,
      info: attentionKindInfo(kind),
      items,
      total: amounts.length === 0 ? undefined : sumDecimalStrings(amounts),
    };
  });
}

const BADGE = "rounded bg-amber-100 px-1.5 py-0.5 text-xs text-amber-800";

function AttentionItem({
  item,
  orgs,
}: {
  item: CashflowAttention;
  orgs: CashflowOrg[];
}): React.ReactElement {
  // Контрагент часто уже внутри `message` — второй раз его не печатаем.
  const kontragent =
    item.kontragent && !item.message.includes(item.kontragent) ? item.kontragent : null;
  return (
    <li className="space-y-0.5">
      <p className="text-sm">{item.message}</p>
      <p className="flex flex-wrap gap-x-2 text-xs text-muted-foreground">
        {kontragent !== null && <span>{kontragent}</span>}
        {item.doc && <span>{item.doc}</span>}
        {item.org_inn !== null && <span>{orgLabel(item.org_inn, orgs)}</span>}
        {item.amount !== null && (
          <span className="tabular-nums">{formatRub(item.amount)}</span>
        )}
        <span>источник: {item.source}</span>
      </p>
    </li>
  );
}

function AttentionGroupCard({
  group,
  orgs,
}: {
  group: AttentionGroup;
  orgs: CashflowOrg[];
}): React.ReactElement {
  // Короткая группа (≤ 3) и так помещается — раскрыта сразу.
  const [open, setOpen] = useState(group.items.length <= 3);
  const Chevron = open ? ChevronDown : ChevronRight;
  return (
    <div className="rounded-md border">
      <button
        type="button"
        aria-expanded={open}
        onClick={() => setOpen((v) => !v)}
        className="flex w-full items-baseline gap-2 rounded-md px-2 py-1.5 text-left hover:bg-muted focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring"
      >
        <Chevron className="h-4 w-4 shrink-0 self-center" aria-hidden="true" />
        <span className="text-sm font-medium">{group.info.title}</span>
        <span className={BADGE}>{group.items.length}</span>
        {group.total !== undefined && (
          <span className="text-xs tabular-nums text-muted-foreground">
            {formatRub(group.total)}
          </span>
        )}
      </button>
      {group.info.hint !== "" && (
        <p className="px-2 pb-1.5 pl-8 text-xs text-muted-foreground">{group.info.hint}</p>
      )}
      {open && (
        <ul className="space-y-2 border-t px-2 py-2 pl-8">
          {group.items.map((item, i) => (
            <AttentionItem key={`${item.source}-${item.ref ?? "no-ref"}-${i}`} item={item} orgs={orgs} />
          ))}
        </ul>
      )}
    </div>
  );
}

export function AttentionPanel({ attention, sources, orgs }: Props): React.ReactElement {
  const failed = sources.filter((s) => s.status === "failed");
  const groups = groupAttention(attention);
  const actionGroups = groups.filter((g) => g.info.section === "action");
  const infoGroups = groups.filter((g) => g.info.section === "info");
  const actionCount = actionGroups.reduce((n, g) => n + g.items.length, 0);
  const infoCount = infoGroups.reduce((n, g) => n + g.items.length, 0);
  // «Справочно» — свёрнуто: это не работа, а объяснение, почему записи нет.
  const [infoOpen, setInfoOpen] = useState(false);
  const InfoChevron = infoOpen ? ChevronDown : ChevronRight;

  return (
    <Card>
      <CardHeader className="pb-3">
        <CardTitle className="flex items-center gap-2 text-base">
          <AlertTriangle className="h-4 w-4" />
          Требует внимания
          {actionCount > 0 && <span className={BADGE}>{actionCount}</span>}
        </CardTitle>
      </CardHeader>
      <CardContent className="space-y-3">
        {failed.map((s) => (
          <div
            key={s.name}
            className="rounded-md border border-rose-300 bg-rose-50 p-3 text-sm text-rose-800"
          >
            Источник «{s.name}» не отработал: {s.message ?? "причина не указана"}. Календарь
            показан без его записей.
          </div>
        ))}

        {attention.length === 0 && <p className="text-sm text-muted-foreground">Замечаний нет</p>}

        {actionGroups.length > 0 && (
          <section className="space-y-2">
            <h3 className="text-sm font-semibold">Требует действий</h3>
            {actionGroups.map((g) => (
              <AttentionGroupCard key={g.kind} group={g} orgs={orgs} />
            ))}
          </section>
        )}

        {infoGroups.length > 0 && (
          <section className="space-y-2">
            <button
              type="button"
              aria-expanded={infoOpen}
              onClick={() => setInfoOpen((v) => !v)}
              className="flex items-center gap-2 text-sm font-semibold hover:underline"
            >
              <InfoChevron className="h-4 w-4" aria-hidden="true" />
              Справочно
              <span className="rounded bg-slate-100 px-1.5 py-0.5 text-xs font-normal text-slate-700">
                {infoCount}
              </span>
            </button>
            {infoOpen &&
              infoGroups.map((g) => <AttentionGroupCard key={g.kind} group={g} orgs={orgs} />)}
          </section>
        )}
      </CardContent>
    </Card>
  );
}
